import { useEffect, useState } from "react";
import { BookOpen, ArrowDownLeft, ArrowUpRight, TrendingUp, TrendingDown, Printer, FileDown } from "lucide-react";
import { api, inr } from "@/lib/api";

const RANGES = [[7, "7 din"], [30, "30 din"], [90, "90 din"]];

export default function AdminCashbook() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ totals: { in: 0, out: 0, net: 0 }, days: [] });
  useEffect(() => { api.get(`/admin/cashbook?days=${days}`).then((r) => setData(r.data)).catch(() => {}); }, [days]);
  const T = data.totals;

  const exportCSV = () => {
    const rows = [["Date", "Direction", "Source", "Label", "Party", "Method", "Amount"]];
    data.days.forEach((d) => d.entries.forEach((e) => rows.push([d.date, e.kind.toUpperCase(), e.source, e.label, e.party, e.method, e.amount])));
    rows.push([], ["", "", "", "", "", "Total In", T.in], ["", "", "", "", "", "Total Out", T.out], ["", "", "", "", "", "Net", T.net]);
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `cashbook-${days}d.csv`; a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-6" data-testid="cashbook-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2"><BookOpen className="text-emerald-600" /> Cashbook</h1>
        <div className="flex items-center gap-2">
          <button data-testid="cb-print-btn" onClick={() => window.open(`/print/cashbook?days=${days}`, "_blank")} className="px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 hover:bg-slate-900"><Printer size={13} /> Print</button>
          <button data-testid="cb-csv-btn" onClick={exportCSV} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 hover:bg-emerald-700"><FileDown size={13} /> CSV</button>
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full p-0.5">
            {RANGES.map(([d, label]) => (
              <button key={d} data-testid={`cb-range-${d}`} onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold ${days === d ? "bg-emerald-600 text-white" : "text-slate-500"}`}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="cb-total-in">
          <p className="flex items-center gap-1.5 text-emerald-600 text-sm font-bold"><ArrowDownLeft size={15} /> Paisa Aaya (In)</p>
          <p className="text-2xl font-extrabold text-slate-900 mt-1">{inr(T.in)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="cb-total-out">
          <p className="flex items-center gap-1.5 text-amber-600 text-sm font-bold"><ArrowUpRight size={15} /> Paisa Gaya (Out)</p>
          <p className="text-2xl font-extrabold text-slate-900 mt-1">{inr(T.out)}</p>
        </div>
        <div className={`rounded-2xl p-5 text-white ${T.net >= 0 ? "bg-emerald-700" : "bg-red-600"}`} data-testid="cb-net">
          <p className="flex items-center gap-1.5 text-white/80 text-sm font-bold">{T.net >= 0 ? <TrendingUp size={15} /> : <TrendingDown size={15} />} Net · Cash in Hand</p>
          <p className="text-2xl font-extrabold mt-1">{inr(T.net)}</p>
          <p className="text-white/70 text-xs mt-0.5" data-testid="cb-closing-balance">Closing balance: {inr(data.closing_balance ?? 0)}</p>
        </div>
      </div>

      <div className="space-y-4">
        {data.days.length === 0 && <p className="text-center text-slate-400 text-sm py-12">Is period me koi cash movement nahi</p>}
        {data.days.map((d) => (
          <div key={d.date} data-testid={`cb-day-${d.date}`} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
              <div>
                <p className="font-bold text-slate-800">{new Date(d.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</p>
                <p className="text-[11px] text-slate-400 font-semibold">Opening {inr(d.opening ?? 0)} → Closing {inr(d.closing ?? 0)}</p>
              </div>
              <div className="flex items-center gap-3 text-sm font-bold">
                <span className="text-emerald-600">+{inr(d.in)}</span>
                <span className="text-amber-600">-{inr(d.out)}</span>
                <span className={`px-2 py-0.5 rounded-lg ${d.net >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{inr(d.net)}</span>
              </div>
            </div>
            <div className="divide-y divide-slate-50">
              {d.entries.map((e, i) => (
                <div key={`${e.source}-${i}`} className="flex items-center gap-3 px-4 py-2.5">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${e.kind === "in" ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"}`}>
                    {e.kind === "in" ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-slate-800 truncate">{e.label}</p>
                    <p className="text-xs text-slate-400 truncate">{e.party} · {e.method?.toUpperCase()}</p>
                  </div>
                  <p className={`font-extrabold text-sm shrink-0 ${e.kind === "in" ? "text-emerald-600" : "text-amber-600"}`}>{e.kind === "in" ? "+" : "-"}{inr(e.amount)}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
